import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PlayIcon } from '@heroicons/react/24/outline';
import { NewPlayPayload, DefaultService, ReactionsResponse, FeedPlayerInfo } from '../../../generated-api';
import { ReactionButton } from '../../reactions/ReactionButton';
import { FeedPlayDetails, FeedPlayResult, FeedRatingImpact, formatDuration, formatRelativeTime } from '../feedUtils';

interface NewPlayCardProps {
  payload: NewPlayPayload;
  createdAt: string;
  initialReactions?: ReactionsResponse;
  details?: FeedPlayDetails;
}

const MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

const Avatar: React.FC<{ player: FeedPlayerInfo }> = ({ player }) =>
  player.playerAvatar ? (
    <img src={player.playerAvatar} alt="" className="h-6 w-6 rounded-full object-cover flex-shrink-0" />
  ) : (
    <span className="h-6 w-6 rounded-full bg-surface-skeleton flex items-center justify-center text-[11px] font-medium text-body flex-shrink-0">
      {player.playerName.charAt(0).toUpperCase()}
    </span>
  );

const formatDelta = (delta: number) => {
  const rounded = Math.round(delta * 10) / 10;
  if (rounded === 0) return '±0';
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toFixed(1)}`;
};

/** Rating gained or lost: green up, red down. */
const RatingDelta: React.FC<{ delta: number }> = ({ delta }) => {
  const text = formatDelta(delta);
  const tone = text === '±0' ? 'text-muted' : delta > 0 ? 'text-green-700' : 'text-red-700';
  return (
    <span className={`text-sm font-medium tabular-nums ${tone}`} aria-label={`Rating ${text}`}>
      {text}
    </span>
  );
};

/** Global position after the play and how it moved; nothing until the rating is certain. */
const RankMove: React.FC<{ impact: FeedRatingImpact }> = ({ impact }) => {
  const { rankBefore, rankAfter } = impact;
  if (rankAfter === null) return null;
  const moved = rankBefore === null ? null : rankBefore - rankAfter;
  let label = `Global position ${rankAfter}`;
  let move: React.ReactNode = null;
  if (rankBefore === null) {
    label += ', newly ranked';
    move = <span className="text-primary-700">new</span>;
  } else if (moved && moved > 0) {
    label += `, up ${moved}`;
    move = <span className="text-green-700">▲{moved}</span>;
  } else if (moved && moved < 0) {
    label += `, down ${-moved}`;
    move = <span className="text-red-700">▼{-moved}</span>;
  }
  return (
    <span className="text-xs tabular-nums text-muted whitespace-nowrap" aria-label={label} title={label}>
      #{rankAfter}{move && <> {move}</>}
    </span>
  );
};

const ResultRow: React.FC<{ result: FeedPlayResult; impacts?: Record<string, FeedRatingImpact> }> = ({ result, impacts }) => {
  const winner = result.place === 1;
  const rowImpacts = result.players
    .map(p => impacts?.[p.playerId])
    .filter((i): i is FeedRatingImpact => !!i);
  // A one-player row shows its position; team rows only the rating changes.
  const solo = result.players.length === 1 ? rowImpacts[0] : undefined;
  return (
    <li className={`flex items-center gap-2 py-1 ${winner ? '' : 'text-body'}`} data-testid="feed-result-row">
      <span className="w-6 text-center text-sm flex-shrink-0" aria-label={result.place ? `Place ${result.place}` : undefined}>
        {result.place ? MEDALS[result.place] || <span className="text-xs text-muted tabular-nums">{result.place}.</span> : ''}
      </span>
      <span className="flex -space-x-1.5">
        {result.players.slice(0, 3).map(p => <Avatar key={p.playerId} player={p} />)}
      </span>
      <span className={`flex-1 min-w-0 truncate text-sm ${winner ? 'font-semibold text-heading' : ''}`}>
        {result.players.map(p => p.playerName).join(' & ')}
      </span>
      {solo && <RankMove impact={solo} />}
      {rowImpacts.length > 0 && (
        <span className="flex-shrink-0 flex gap-1">
          {rowImpacts.map((impact, i) => <RatingDelta key={i} delta={impact.delta} />)}
        </span>
      )}
    </li>
  );
};

/** Results from the event alone (old events, or before play details load): winners first. */
const fallbackResults = (payload: NewPlayPayload): FeedPlayResult[] => {
  const winners = new Set(payload.winners || []);
  const hasWinners = winners.size > 0;
  return [...payload.players]
    .sort((a, b) => Number(winners.has(b.playerName)) - Number(winners.has(a.playerName)))
    .map(p => ({ place: hasWinners && winners.has(p.playerName) ? 1 : null, players: [p] }));
};

export const NewPlayCard: React.FC<NewPlayCardProps> = ({ payload, createdAt, initialReactions, details }) => {
  const [reactionsData, setReactionsData] = useState<ReactionsResponse | null>(initialReactions || null);

  const fetchReactions = useCallback(async () => {
    try {
      const response = await DefaultService.getPlayReactionsPlaysPlayIdReactionsGet({
        playId: payload.playId,
      });
      setReactionsData(response);
    } catch (err) {
      // Silently fail - reactions are not critical
      console.error('Failed to fetch reactions:', err);
    }
  }, [payload.playId]);

  // Only fetch if initialReactions not provided
  useEffect(() => {
    if (!initialReactions) {
      fetchReactions();
    }
  }, [initialReactions, fetchReactions]);

  const handleReactionChange = useCallback(() => {
    // Refresh reactions when user adds/changes a reaction
    fetchReactions();
  }, [fetchReactions]);

  const results = details && details.results.length > 0 ? details.results : fallbackResults(payload);
  const meta = [
    formatRelativeTime(details?.playDate || createdAt),
    formatDuration(details?.durationMinutes),
    ...(details?.leagueNames || []),
  ].filter(Boolean);

  return (
    <article className="bg-surface rounded-lg shadow-sm border border-edge px-3 pt-3 pb-2">
      <div className="flex items-center gap-3">
        <Link
          data-track="feed_new_play_card"
          to={`/plays/${payload.playId}`}
          className="flex items-center gap-3 min-w-0 flex-1"
        >
          {payload.gameImage ? (
            <img
              src={payload.gameImage}
              alt=""
              className="h-12 w-12 rounded-md object-cover flex-shrink-0 bg-surface-skeleton"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span className="h-12 w-12 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
              <PlayIcon className="h-5 w-5 text-primary-600" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="sr-only">New Play Tracked</p>
            <h3 className="font-serif font-semibold text-base text-heading truncate">{payload.gameName}</h3>
            <p className="text-xs text-muted truncate">{meta.join(' · ')}</p>
          </div>
        </Link>
        {/* ReactionButton outside the Links to prevent navigation interference */}
        <div className="flex-shrink-0 flex items-center min-h-[44px]">
          <ReactionButton
            playId={payload.playId}
            reactionSummary={reactionsData?.summary}
            userReaction={reactionsData?.userReaction}
            onReactionChange={handleReactionChange}
          />
        </div>
      </div>
      <Link to={`/plays/${payload.playId}`} tabIndex={-1} className="block mt-1">
        <ol aria-label="Results">
          {results.map((r, i) => <ResultRow key={i} result={r} impacts={details?.ratingImpacts} />)}
        </ol>
      </Link>
    </article>
  );
};
